import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { KPFAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { WorkTrackingSingleTaetigkeitenTypes, WorkTrackingTaetigkeit } from '../entitites/WorkTrackingTaetigkeit.js';
import { WorkTrackingTaetigkeitenState } from '../entitites/WorkTrackingTaetigkeitenState.js';

/**
 * @customElement chancen-component-work-tracking-add-single
 */
@customElement('chancen-component-work-tracking-add-single')
export class ChancenComponentCardTitle extends ChemistryLitElement {
  @state() private _selectedKPFID = new RXController(this, null as Maybe<string>);
  @state() private _selectedPersonIDs = new RXController(this, [] as string[]);
  @state() private _selectedActivity = new RXController(this, '');
  @state() private _selectedDate = new RXController(this, DateTime.now());
  @state() private _selectedAmount = new RXController(this, 0);
  @state() private _allowFreeType = new RXController(this, true);
  @state() private _types = new RXController(this, Object.values(WorkTrackingSingleTaetigkeitenTypes) as string[]);

  private _renderActivityInput() {
    if (!this._types.value) {
      return html` <cr-input
        ?disabled=${!this._selectedKPFID.value || !this._selectedPersonIDs.value.length}
        placeholder="Aktivität"
        .value=${this._selectedActivity.value}
        @value-picked=${(e: ValueEvent<Maybe<string>>) => {
          if (!e.detail.value) {
            return;
          }

          this._selectedActivity.next(e.detail.value);
        }}
      ></cr-input>`;
    }

    const selection = html` <cr-dropdown-selection
      ?disabled=${!this._selectedKPFID.value || !this._selectedPersonIDs.value.length}
      .selectables=${this._types.value}
      placeholder="Art"
      clearable
      .value=${this._selectedActivity.value}
      @value-picked=${(e: ValueEvent<Maybe<string>>) => {
        if (!e.detail.value) {
          return;
        }
        this._selectedActivity.next(e.detail.value);
        this.requestUpdate();
      }}
    ></cr-dropdown-selection>`;

    if (!this._allowFreeType.value) {
      return selection;
    }

    const free = html`
      <cr-input
        ?disabled=${!this._selectedKPFID.value || !this._selectedPersonIDs.value.length}
        clearable
        placeholder="Art-Freitext"
        .value=${this._selectedActivity.value}
        @value-changed=${(e: ValueEvent<Maybe<string>>) => {
          if (!e.detail.value) {
            return;
          }
          this._selectedActivity.next(e.detail.value);
          this.requestUpdate();
        }}
      ></cr-input>
    `;

    if (!this._selectedActivity.value || this._selectedActivity.value.length === 0) {
      return [selection, free];
    }

    if (this._types.value.includes(this._selectedActivity.value ?? '')) {
      return selection;
    } else {
      return free;
    }
  }

  override render() {
    return html` <cr-card flex ${css({ maxWidth: '300px' })} space="md" padding="md">
      <chancen-component-card-title>Tätigkeiten Eintragen</chancen-component-card-title>
      <cr-text>Hier kannst du manuell Tätigkeiten für Personen eintragen.</cr-text>
      <cr-flex space="sm" ${css({ maxWidth: '400px' })}>
        <x-entity-picker
          flex
          placeholder="KPF"
          clearable
          .options=${{
            class: KPF,
            operation: KPFAPI.subscribeByFilter,
            selectionSet: { name: 1, kuerzel: 1, produkt: { _class: 1, kuerzel: 1 }, school: { name: 1, kuerzel: 1 } },
            filter: new AdornisFilter({}),
          }}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => {
            if (!e.detail.value) {
              return;
            }

            this._selectedKPFID.next(e.detail.value);
            this.requestUpdate();
          }}
        ></x-entity-picker>
        <x-multi-entity-picker
          ?disabled=${!this._selectedKPFID.value}
          ${css({ maxWidth: '720px' })}
          placeholder="Person"
          clearable
          .options=${{
            class: ChancenwerkUser,
            selectionSet: { vorname: 1, nachname: 1 },
            operation: UserAPI.subscribeByFilter,
            filter: new ChancenwerkUserFilter({
              userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
              aspects: [new KPFAspect({ customID: 'kpfs', value: this._selectedKPFID.value ?? '' })],
            }),
          }}
          @value-picked=${async (e: ValueEvent<Maybe<string[]>>) => {
            if (!e.detail.value) {
              return;
            }

            this._selectedPersonIDs.next(e.detail.value);
          }}
        ></x-multi-entity-picker>
        <cr-date-picker-dropdown
          ?disabled=${!this._selectedKPFID.value || !this._selectedPersonIDs.value.length}
          placeholder="Datum"
          .value=${this._selectedDate.value}
          @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
            if (!e.detail.value) {
              return;
            }

            this._selectedDate.next(e.detail.value);
          }}
        ></cr-date-picker-dropdown>
        ${this._renderActivityInput()}
        <cr-input
          ?disabled=${!this._selectedKPFID.value || !this._selectedPersonIDs.value.length}
          placeholder="Entschädigung (€)"
          type="number"
          @value-changed=${(e: ValueEvent<Maybe<number>>) => {
            if (!e.detail.value) {
              return;
            }

            this._selectedAmount.next(e.detail.value);
          }}
        ></cr-input>
        <cr-button
          ?disabled=${!this._selectedKPFID.value ||
          !this._selectedPersonIDs.value.length ||
          !this._selectedActivity.value ||
          !this._selectedAmount.value}
          @click=${async () => {
            for (const personID of this._selectedPersonIDs.value) {
              new WorkTrackingTaetigkeit({
                userID: personID,
                generatedFromID: WorkTrackingTaetigkeit.NOT_GENERATED,
                stateContractor: WorkTrackingTaetigkeitenState.OPEN,
                stateResponsible: WorkTrackingTaetigkeitenState.FINISHED,
                type: this._selectedActivity.value,
                kpfID: this._selectedKPFID.value,
                date: this._selectedDate.value.toJSDate(),
                money: this._selectedAmount.value,
                isSingle: true,
              }).create();
            }
          }}
          >Hinzufügen</cr-button
        >
      </cr-flex>
    </cr-card>`;
  }
}
